import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

/**
 * One code the surgeon MOTIVATED FOR or PLANNED — work that has not happened yet.
 *
 * Deliberately not the same shape as a billing `ProcedureCode`: these rows live in
 * `PlannedProcedureCodes`, which nothing in the billing path reads. Keeping the two types
 * distinct on the client is the cheapest way to stop one being passed where the other is meant.
 */
export interface PlannedProcedureCode {
  id: string;
  groupId: string;
  patientId: string;
  appointmentId: string | null;
  sourceAiRequestId: string | null;
  code: string;
  description: string | null;
  quantity: number;
  displayIndex: number;
  stage: 'motivated' | 'planned';
  capturedProcedureCodeId: string | null;
  active: boolean;
}

/**
 * A letter of motivation whose codes are not yet on any booking — what the §3d booking screen
 * prefills from. Grouped by AI request, because the thing the user recognises is "the motivation
 * I wrote on the 12th", not five separate rows.
 *
 * `codes` carries NO row id. §3d (2026-09-01): the server now sources this list from
 * `AIRequest.motivationProcedureCodes` — the letter itself — rather than from
 * `PlannedProcedureCode` rows, so that a letter reaches the booking screen whether or not the
 * "Book this theatre case?" prompt was ever accepted. Most offered letters therefore have no
 * rows behind them yet, and an `id` here would be one that exists for some entries and not
 * others. The booking screen sends back `sourceAiRequestId`, never row ids.
 */
export interface UnconsumedMotivation {
  sourceAiRequestId: string;
  patientId: string;
  groupId: string;
  motivatedAt: string;
  codes: Array<Pick<PlannedProcedureCode, 'code' | 'description' | 'quantity' | 'displayIndex'>>;
}

export interface PlannedProcedureCodeResponse {
  success: boolean;
  message?: string;
  data: PlannedProcedureCode[];
  /** Only on the ai-request lookup: true once the "book this case?" offer has been answered. */
  answered?: boolean;
  created?: boolean | number;
  /** Only on `replaceForAppointment` — what the replace-set actually did. */
  adopted?: number;
  removed?: number;
}

/**
 * One code as the booking screen holds it. Deliberately the narrow shape the server stores
 * rather than the full `ProcedureCode` the chip selector emits: the selector rides a dozen
 * billing-only fields along (`paidCash`, `randValue`, `assistantName`, …) and none of them
 * belong on work that has not happened.
 */
export interface PlannedCodeInput {
  code: string;
  description?: string | null;
  quantity?: number;
}

export interface UnconsumedMotivationResponse {
  success: boolean;
  message?: string;
  data: UnconsumedMotivation[];
}

/** One line of the note's coding bundle, as it exists once the note has been saved. */
export interface CapturedCodeRef {
  /** The `PatientProcedureCode` row id. Absent for a line the save did not persist. */
  id?: string;
  code: string;
  description?: string | null;
  /** `'modifier'` when the codebook says so — the server re-checks, it does not trust this. */
  type?: string;
}

export interface CapturePayload {
  patientNoteId?: string;
  aiRequestId?: string;
  appointmentId?: string;
  patientId?: string;
  /**
   * The note's own date. Must be the SAME value the pre-save `compareForNote` was given —
   * it anchors the §3d date-window match, and two different anchors could resolve the dialog
   * and the post-save banner to two different bookings for one note.
   */
  noteDate?: string;
  capturedCodes: CapturedCodeRef[];
}

/**
 * The two-direction divergence (§7). `modifiersNotPlanned` is reported separately and
 * deliberately does NOT count as a divergence: modifiers qualify a captured line rather than
 * being planned work, and warning on them is how a banner earns a reputation for crying wolf.
 */
/**
 * Which planned set the answer was measured against.
 *
 * `'inferred-appointment'` (§3d) is the one to render differently: the booking was matched by
 * DATE, not by a link the user made, so the warning must say so. See
 * `utils/theatreAppointmentLookup.js` for why that inference is allowed at all and what it is
 * forbidden from doing.
 */
export type ComparedAgainst = 'appointment' | 'inferred-appointment' | 'motivation' | 'nothing';

export interface CaptureResponse {
  success: boolean;
  /** Always false. The note is already saved by the time this call is made. */
  blocked: boolean;
  message?: string;
  appointmentId: string | null;
  comparedAgainst?: ComparedAgainst;
  /** True when the booking was matched by date. Nothing is written back in that case. */
  inferred?: boolean;
  inferenceReason?: 'unique-match' | 'ambiguous' | 'none-in-window' | 'insufficient-input' | null;
  plannedCount?: number;
  capturedCount?: number;
  written?: number;
  linked?: Array<{ plannedId: string; capturedProcedureCodeId: string; code: string }>;
  hasDivergence: boolean;
  warnings: {
    capturedNotPlanned: Array<{ code: string; description: string | null }>;
    plannedNotCaptured: Array<{ id: string; code: string; description: string | null; quantity: number }>;
    modifiersNotPlanned: Array<{ code: string; description: string | null }>;
  };
}

/**
 * §3d — the answer to `compareForNote`. The same comparison as `CaptureResponse` with nothing
 * written, so it carries no `written`/`linked`/`blocked`, and it adds the booking's own code
 * list so the dialog can offer "use the booking's codes" without a second round trip.
 */
export interface CompareResponse {
  success: boolean;
  message?: string;
  appointmentId: string | null;
  comparedAgainst: ComparedAgainst;
  inferred: boolean;
  inferenceReason?: 'unique-match' | 'ambiguous' | 'none-in-window' | 'insufficient-input' | null;
  plannedCount: number;
  capturedCount: number;
  plannedCodes: Array<{ id: string; code: string; description: string | null; quantity: number }>;
  hasDivergence: boolean;
  warnings: {
    capturedNotPlanned: Array<{ code: string; description: string | null }>;
    plannedNotCaptured: Array<{ id: string; code: string; description: string | null; quantity: number }>;
    modifiersNotPlanned: Array<{ code: string; description: string | null }>;
  };
}

export interface ComparePayload {
  aiRequestId?: string;
  appointmentId?: string;
  patientId?: string;
  /** The note's own date, so the date-window match is anchored on the note and not on today. */
  noteDate?: string;
  capturedCodes: CapturedCodeRef[];
}

/**
 * Client for `controllers/plannedProcedureCodesController.js`.
 *
 * Tasks C2/C3 of `tasks/theatre-booking-and-procedure-lifecycle.md` §7 — the motivation →
 * booking half of the procedure-code lifecycle.
 *
 * Two things about this service are load-bearing rather than incidental:
 *
 *   1. **`createFromAiRequest` sends only an id.** The codes come off
 *      `AIRequest.motivationProcedureCodes` on the server — "what was actually on the letter",
 *      written at the moment the letter was generated. A client-supplied list would be a
 *      second, weaker source for the same fact.
 *
 *   2. **`hasAnsweredOffer` is a question about the database, not about this tab.** It is how a
 *      declined "Book this theatre case?" stays declined across a reload, another device and
 *      another user. Nothing here caches the answer.
 */
@Injectable({
  providedIn: 'root'
})
export class PlannedProcedureCodeService {
  private apiUrl = `api/planned-procedure-codes`;

  constructor(private http: HttpClient) { }

  private authHeaders() {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  }

  /** Every live planned code for a patient, both stages. */
  loadByPatient(patientId: string): Observable<PlannedProcedureCodeResponse> {
    return this.http.get<PlannedProcedureCodeResponse>(`${this.apiUrl}/patient/${patientId}`, {
      headers: this.authHeaders()
    });
  }

  /** What a booking is planned to do — the Netcare form's CPT grid reads this. */
  loadByAppointment(appointmentId: string): Observable<PlannedProcedureCodeResponse> {
    return this.http.get<PlannedProcedureCodeResponse>(`${this.apiUrl}/appointment/${appointmentId}`, {
      headers: this.authHeaders()
    });
  }

  /**
   * Whether the "Book this theatre case?" offer for this AI request has already been answered —
   * accepted OR declined. `includeInactive=true` is what makes a decline visible: it is recorded
   * as inactive rows, so a query filtered to live rows would offer again on the next reload.
   */
  hasAnsweredOffer(aiRequestId: string): Observable<PlannedProcedureCodeResponse> {
    return this.http.get<PlannedProcedureCodeResponse>(
      `${this.apiUrl}/ai-request/${aiRequestId}?includeInactive=true`,
      { headers: this.authHeaders() }
    );
  }

  /** The C3 offer list: motivations for this patient not yet attached to any appointment. */
  loadUnconsumedMotivations(patientId: string): Observable<UnconsumedMotivationResponse> {
    return this.http.get<UnconsumedMotivationResponse>(
      `${this.apiUrl}/patient/${patientId}/unconsumed-motivations`,
      { headers: this.authHeaders() }
    );
  }

  /** Accepted the offer — creates the `stage='motivated'` rows. Idempotent per AI request. */
  createFromAiRequest(aiRequestId: string): Observable<PlannedProcedureCodeResponse> {
    return this.http.post<PlannedProcedureCodeResponse>(`${this.apiUrl}/from-ai-request`, { aiRequestId }, {
      headers: this.authHeaders()
    });
  }

  /**
   * Declined the offer. Persists the decision server-side so it is never made again for this
   * AI request — see the controller header for why this is a row rather than a column.
   */
  dismissOffer(aiRequestId: string): Observable<PlannedProcedureCodeResponse> {
    return this.http.post<PlannedProcedureCodeResponse>(`${this.apiUrl}/from-ai-request/dismiss`, { aiRequestId }, {
      headers: this.authHeaders()
    });
  }

  /**
   * C3 — sets `stage='planned'` and `appointmentId`. An UPDATE, never an insert, so attaching
   * the same motivation twice cannot double the rows.
   */
  attachToAppointment(appointmentId: string, sourceAiRequestId: string): Observable<PlannedProcedureCodeResponse> {
    return this.http.post<PlannedProcedureCodeResponse>(
      `${this.apiUrl}/attach`,
      { appointmentId, sourceAiRequestId },
      { headers: this.authHeaders() }
    );
  }

  /** The reverse: back to `stage='motivated'` with no appointment, and offered again. */
  detachFromAppointment(payload: { appointmentId?: string; sourceAiRequestId?: string; ids?: string[] }): Observable<PlannedProcedureCodeResponse> {
    return this.http.post<PlannedProcedureCodeResponse>(`${this.apiUrl}/detach`, payload, {
      headers: this.authHeaders()
    });
  }

  /**
   * §3d — THE CODES THIS BOOKING PLANS, AS THE USER LEFT THEM.
   *
   * REPLACE-SET: `codes` is the whole intended list, so an empty array clears the booking and
   * that is a legal thing to ask for. The server refuses a request with no `codes` key at all,
   * which is what stops a malformed body emptying a hospital's code grid by accident.
   *
   * `sourceAiRequestId` names the motivation whose codes were prefilled, when there was one. It
   * is not "where these codes came from" — it is permission to ADOPT that motivation's existing
   * rows instead of creating lookalikes beside them, which is what keeps the letter's
   * provenance and stops the motivation being offered again on the next booking. Codes the
   * server has to create are always recorded with no source.
   */
  replaceForAppointment(
    appointmentId: string,
    codes: PlannedCodeInput[],
    sourceAiRequestId?: string | null,
  ): Observable<PlannedProcedureCodeResponse> {
    return this.http.put<PlannedProcedureCodeResponse>(
      `${this.apiUrl}/appointment/${appointmentId}`,
      { codes, sourceAiRequestId: sourceAiRequestId || null },
      { headers: this.authHeaders() }
    );
  }

  /**
   * §3d — the divergence answer WHILE THE NOTE DIALOG IS STILL OPEN.
   *
   * Identical comparison to `captureFromNote`, sharing the server's resolution and diff, but it
   * writes nothing. The point is timing: `captureFromNote` runs once the note is in the
   * timeline, which is too late for the surgeon to do anything about the answer except file a
   * correction. This runs while the chips are still editable.
   */
  compareForNote(payload: ComparePayload): Observable<CompareResponse> {
    return this.http.post<CompareResponse>(`${this.apiUrl}/compare`, payload, {
      headers: this.authHeaders()
    });
  }

  /**
   * C4 — called AFTER a procedure note has saved, with the `PatientProcedureCode` rows the save
   * produced. Writes `capturedProcedureCodeId` back onto the planned rows and answers with the
   * divergence in both directions.
   *
   * **This can never block a save.** It is issued once the note is already in the timeline, and
   * the response is rendered as a dismissible banner. A failure here costs a warning, not a
   * clinical record — which is why the caller does not await it before telling the user the
   * note saved.
   */
  captureFromNote(payload: CapturePayload): Observable<CaptureResponse> {
    return this.http.post<CaptureResponse>(`${this.apiUrl}/capture`, payload, {
      headers: this.authHeaders()
    });
  }

  /** Soft-deletes one row. Not the same thing as declining an offer. */
  deactivate(id: string): Observable<{ success: boolean; message?: string }> {
    return this.http.delete<{ success: boolean; message?: string }>(`${this.apiUrl}/${id}`, {
      headers: this.authHeaders()
    });
  }
}
